import { TimerReset } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { api, expireSession } from '@/lib/api/client'
import { IDLE_LIMIT_MS, IDLE_WARN_MS, lastActivity, markActivity, resetActivity } from '@/lib/auth/activity'
import { authApi } from '@/lib/auth/api'
import { tokens } from '@/lib/auth/tokens'

const EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const
/** Kullanıcı etkinken istek atmasa da (form dolduruyor) oturumu canlı tutmak için en çok bu aralıkla ping. */
const PING_EVERY_MS = 4 * 60_000
const TICK_MS = 5_000

/**
 * Hareketsizlik denetimi: işlem olduğu sürece oturum açık kalır (gerekirse sunucuya ping); 15 dk hiç işlem yoksa
 * 2 dk önce uyarı, süre dolunca çıkış. Etkinlik sekmeler arasında paylaşılır.
 */
export function IdleSessionGuard() {
  const { t } = useTranslation()
  const [remaining, setRemaining] = useState<number | null>(null)
  const lastPing = useRef(0)

  useEffect(() => {
    resetActivity()
    lastPing.current = Date.now()
    const onActivity = () => markActivity()
    const onVisible = () => document.visibilityState === 'visible' && markActivity()
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    document.addEventListener('visibilitychange', onVisible)

    const timer = window.setInterval(() => {
      const now = Date.now()
      const idle = now - lastActivity()
      if (idle >= IDLE_LIMIT_MS) {
        window.clearInterval(timer)
        const rt = tokens.refresh
        void (rt ? authApi.logout(rt).catch(() => undefined) : Promise.resolve()).finally(() => expireSession('SESSION_EXPIRED'))
        return
      }
      setRemaining(idle >= IDLE_LIMIT_MS - IDLE_WARN_MS ? IDLE_LIMIT_MS - idle : null)
      // Etkin kullanıcı istek atmıyorsa sunucu süresini tazele (ping kullanıcı kaynaklı sayılır).
      if (idle < PING_EVERY_MS && now - lastPing.current >= PING_EVERY_MS) {
        lastPing.current = now
        void api.get('/api/v1/auth/ping').catch(() => undefined)
      }
    }, TICK_MS)

    return () => {
      window.clearInterval(timer)
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity))
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (remaining == null) return null
  const seconds = Math.max(0, Math.ceil(remaining / 1000))
  const stay = () => {
    resetActivity()
    lastPing.current = Date.now()
    setRemaining(null)
    void api.get('/api/v1/auth/ping').catch(() => undefined)
  }
  return (
    <Modal
      open
      onOpenChange={(open) => !open && stay()}
      icon={<TimerReset />}
      title={t('auth.idle.title')}
      description={t('auth.idle.body', { time: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` })}
      footer={
        <Button type="button" onClick={stay}>
          {t('auth.idle.stay')}
        </Button>
      }
    />
  )
}
